export const API_BASE = 'https://smartface.edusoft.top/api/';
export const getApiBase = () => API_BASE;

export const api = {
  
  async getLogs(date = null) {
    const queryString = date ? `?date=${date}` : '';
    return this.fetch(`get-logs${queryString}`);
  },

  async fetch(endpoint, options = {}) {
    try {
      // 1. ดึง Token จาก localStorage
      const token = localStorage.getItem('auth_token');
      
      // 2. เตรียม Headers
      const defaultHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      };

      // 3. ถ้ามี Token ให้แนบไปกับ Authorization Header
      if (token) {
        defaultHeaders['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE}${endpoint}`, {
        headers: {
          ...defaultHeaders,
          ...options.headers
        },
        ...options
      });

      // [✅ เพิ่มการจัดการ 401] ถ้า Token ผิด/หมดอายุ ให้ล้างค่าแล้วเตะไปหน้าล็อกอิน
      if (response.status === 401) {
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
        return Promise.reject(new Error('เลขบัตรประชาชนหรือรหัสบุคลากรไม่ถูกต้อง'));
      }

      if (!response.ok) {
        let errorMessage = 'เกิดข้อผิดพลาดในการประมวลผล';
        const errorText = await response.text(); 
        
        try {
          const errorData = JSON.parse(errorText);
          errorMessage = errorData.error || errorData.message || errorMessage;
        } catch (jsonError) {
          if (errorText.trim()) {
            errorMessage = `Server error: ${errorText.substring(0, 100)}...`;
          }
        }
        throw new Error(errorMessage);
      }

      const responseText = await response.text();
      if (!responseText.trim()) {
        throw new Error('ไม่ได้รับการตอบสนองจากเซิร์ฟเวอร์');
      }

      return JSON.parse(responseText);
    } catch (error) {
      console.error(`API Error (${endpoint}):`, error);
      throw error;
    }
  },

  async login(credentials) {
    return this.fetch('auth', {
      method: 'POST',
      body: JSON.stringify(credentials)
    });
  },

  async getEmployees() {
    return this.fetch('employees');
  },

  async addEmployee(employee) {
    return this.fetch('employees', {
      method: 'POST',
      body: JSON.stringify(employee)
    });
  },

  async updateEmployee(employee) {
    return this.fetch('employees', {
      method: 'PUT',
      body: JSON.stringify(employee)
    });
  },

  async deactivateEmployee(employeeId) {
    return this.fetch(`employees?employee_id=${employeeId}`, {
      method: 'DELETE'
    });
  },

  async registerFace(data) {
    return this.fetch('face-register', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async identifyFace(faceDescriptor) {
    return this.fetch('face-identify', {
      method: 'POST',
      body: JSON.stringify({
        face_descriptor: faceDescriptor
      })
    });
  },

  async recognizeFace(data) {
    return this.fetch('face-recognition', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async submitPermissionRequest(requestData) {
    return this.fetch('permission-leave', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
  },
  
  async getMyPermissionRequests(employeeId) {
    return this.fetch(`permission-leave?employee_id=${employeeId}`);
  },
  
  async reportBackPermission(returnData) {
    return this.fetch('permission-leave/return', {
      method: 'PUT',
      body: JSON.stringify(returnData)
    });
  },
  
  async cancelPermissionRequest(cancelData) {
    return this.fetch('permission-leave/cancel', {
      method: 'PUT',
      body: JSON.stringify(cancelData)
    });
  },

  async getReports(params) {
    const queryString = new URLSearchParams(params).toString();
    return this.fetch(`reports?${queryString}`);
  },

  async getOrganizations() {
    return this.fetch('organizations');
  },

  async updateOrganization(organization) {
    return this.fetch('organizations', {
      method: 'PUT',
      body: JSON.stringify(organization)
    });
  },

  async updateAttendanceStatus(attendanceId, status, notes = '', employeeId = null, date = null) {
    return this.fetch('attendance/status', {
      method: 'PUT',
      body: JSON.stringify({
        attendance_id: attendanceId,
        status: status,
        notes: notes,
        employee_id: employeeId,
        date: date
      })
    });
  },
  
  async getLeaveBalance(employeeId) {
    return this.fetch(`leave-balance?employee_id=${employeeId}`);
  },
  
  async getLeaveRequests(employeeId = null) {
    const endpoint = employeeId ? `leave-requests?employee_id=${employeeId}` : 'leave-requests';
    return this.fetch(endpoint);
  },
  
  async submitLeaveRequest(leaveData) {
    return this.fetch('leave-requests', {
      method: 'POST',
      body: JSON.stringify(leaveData)
    });
  },
  
  async approveLeaveRequest(requestId, adminEmployeeId) {
    return this.fetch('leave-approval', {
      method: 'PUT',
      body: JSON.stringify({
        request_id: requestId,
        status: 'approved',
        admin_employee_id: adminEmployeeId
      })
    });
  },

  async getLeaveRequestDetails(requestId) {
    return this.fetch(`leave-request-details?request_id=${requestId}`);
  },
  
  async rejectLeaveRequest(requestId, adminEmployeeId) {
    return this.fetch('leave-approval', {
      method: 'PUT',
      body: JSON.stringify({
        request_id: requestId,
        status: 'rejected',
        admin_employee_id: adminEmployeeId
      })
    });
  },
  
  async getDepartments() {
    return this.fetch('departments');
  },

  async addDepartment(departmentData) {
    return this.fetch('departments', {
      method: 'POST',
      body: JSON.stringify(departmentData)
    });
  },

  async updateDepartmentHead(departmentId, headEmployeeId) {
    return this.fetch('departments', {
      method: 'PUT',
      body: JSON.stringify({
        id: departmentId,
        head_employee_id: headEmployeeId
      })
    });
  },

  async deleteDepartment(departmentId) {
    return this.fetch(`departments?id=${departmentId}`, {
      method: 'DELETE'
    });
  },

  async getHolidays(year) {
    return this.fetch(`holidays?year=${year}`);
  },
  
  async addHoliday(holidayData) {
    return this.fetch('holidays', {
      method: 'POST',
      body: JSON.stringify(holidayData)
    });
  },
  
  async deleteHoliday(holidayId) {
    return this.fetch(`holidays?id=${holidayId}`, {
      method: 'DELETE'
    });
  },

  async submitLateRequest(requestData) {
    return this.fetch('late-requests', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
  },

  async getLateRequests(employeeId = null) {
    const endpoint = employeeId ? `late-requests?employee_id=${employeeId}` : 'late-requests';
    return this.fetch(endpoint);
  },

  async approveLateRequest(requestId, adminEmployeeId) {
    return this.fetch('late-requests-approval', {
      method: 'PUT',
      body: JSON.stringify({
        request_id: requestId,
        status: 'approved',
        admin_employee_id: adminEmployeeId
      })
    });
  },
  
  async rejectLateRequest(requestId, adminEmployeeId) {
    return this.fetch('late-requests-approval', {
      method: 'PUT',
      body: JSON.stringify({
        request_id: requestId,
        status: 'rejected',
        admin_employee_id: adminEmployeeId
      })
    });
  },

  async getLateRequestDetails(requestId) {
    return this.fetch(`late-request-details?request_id=${requestId}`);
  },

  async submitForgotScanRequest(requestData) {
    return this.fetch('forgot-scan-requests', {
      method: 'POST',
      body: JSON.stringify(requestData)
    });
  },

  async getForgotScanRequests(employeeId = null) {
    const endpoint = employeeId ? `forgot-scan-requests?employee_id=${employeeId}` : 'forgot-scan-requests';
    return this.fetch(endpoint);
  },

  async approveForgotScanRequest(requestId, adminEmployeeId) {
    return this.fetch('forgot-scan-requests-approval', {
      method: 'PUT',
      body: JSON.stringify({
        request_id: requestId,
        status: 'approved',
        admin_employee_id: adminEmployeeId
      })
    });
  },
  
  async rejectForgotScanRequest(requestId, adminEmployeeId) {
    return this.fetch('forgot-scan-requests-approval', {
      method: 'PUT',
      body: JSON.stringify({
        request_id: requestId,
        status: 'rejected',
        admin_employee_id: adminEmployeeId
      })
    });
  },

  async getForgotScanRequestDetails(requestId) {
    return this.fetch(`forgot-scan-request-details?request_id=${requestId}`);
  },

  async getOfficialBusinessTrips() {
    return this.fetch('official-business');
  },

  async createOfficialBusinessTrip(data) {
    return this.fetch('official-business', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async updateOfficialBusinessTrip(data) {
    return this.fetch('official-business', {
      method: 'PUT',
      body: JSON.stringify(data)
    });
  },

  async deleteOfficialBusinessTrip(id) {
    return this.fetch(`official-business?id=${id}`, {
      method: 'DELETE'
    });
  },

  async saveManualAttendance(data) {
    return this.fetch('attendance/manual', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  async getExtraWorkDays(year) {
    return this.fetch(`extra-work-days?year=${year}`);
  },
  
  async addExtraWorkDay(data) {
    return this.fetch('extra-work-days', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },
  
  async deleteExtraWorkDay(id) {
    return this.fetch(`extra-work-days?id=${id}`, {
      method: 'DELETE'
    });
  },

  async savePushSubscription(fcmToken, employeeId) {
    return this.fetch('update-fcm-token', { // เปลี่ยนจาก 'save-subscription' เป็น 'update-fcm-token' ให้ตรงกับ index.php
      method: 'POST',
      body: JSON.stringify({
        fcm_token: fcmToken, // ส่งเป็น fcm_token ตามที่ PHP รอรับ
        employee_id: employeeId
      })
    });
  },

  // ==========================================
  // SUPER ADMIN APIs
  // ==========================================
  async getSuperAdminOrganizations() {
    return this.fetch('superadmin/organizations');
  },

  async createOrganizationWithAdmin(data) {
    return this.fetch('superadmin/organizations', {
      method: 'POST',
      body: JSON.stringify(data)
    });
  },

  // ดึงข้อมูลผู้ใช้งานที่อยู่ในเครื่อง ZKTeco
  async getDeviceUsers() {
    return this.fetch('zkteco/device-users'); 
  },

  // 1. ขอตั้งค่าการลงทะเบียน Face ID (ขอ Challenge จาก Server)
  getWebAuthnRegistrationOptions: async (employee_id) => {
    const response = await fetch(`${getApiBase()}webauthn/register-options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee_id })
    });
    if (!response.ok) throw new Error('Failed to get registration options');
    return response.json();
  },

  // 2. ส่งข้อมูลกุญแจที่สแกนสำเร็จไปเก็บบน Database
  savePasskey: async (credentialData) => {
    const response = await fetch(`${getApiBase()}webauthn/save-passkey`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(credentialData)
    });
    if (!response.ok) throw new Error('Failed to save passkey');
    return response.json();
  },

  // 3. ขอ Options สำหรับการสแกนเพื่อเข้าใช้งาน (Authentication)
  getWebAuthnAuthOptions: async (employee_id) => {
    const response = await fetch(`${getApiBase()}webauthn/auth-options`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ employee_id })
    });
    if (!response.ok) throw new Error('Failed to get auth options');
    return response.json();
  }
};

export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (error.message.includes('RECORD_NOT_FOUND')) {
    return 'ไม่พบข้อมูลที่ต้องการยกเลิก อาจถูกลบไปแล้ว';
  } else if (error.message.includes('RECORD_MODIFIED')) {
    return 'ข้อมูลถูกแก้ไขโดยผู้อื่นแล้ว กรุณารีเฟรชหน้าจอ';
  } else if (error.message.includes('TIMEOUT_EXCEEDED')) {
    return 'หมดเวลาในการยกเลิก กรุณาติดต่อผู้ดูแลระบบ';
  } else if (error.message.includes('EMPLOYEE_MISMATCH')) {
    return 'ข้อมูลไม่ตรงกัน กรุณาตรวจสอบอีกครั้ง';
  }
  
  return error.message || 'เกิดข้อผิดพลาดในการเชื่อมต่อ';
};

export default api;